import { useEffect, useState } from "react";
import { Modal, Statistic } from "antd";
import dayjs from "dayjs";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { getCoinStreakStatus } from "../utils/getCoinStreakStatus";
import {
    getWeeklyCoinGoal,
    WEEKLY_COIN_GOAL,
} from "../utils/getWeeklyCoinGoal";
import { parseQuestionTitle } from "../utils/formatQuestionTitle";
import {
    CoinReceiptEntry,
    formatCoinReceiptLine,
    groupCoinReceiptsByTopic,
    readCoinReceipts,
    sumCoinReceipts,
} from "../utils/coinReceipt";

export interface CoinTrendItem {
    date: string;
    coins: number;
}

interface CoinStatsModalProps {
    open: boolean;
    onCancel: () => void;
    totalCoins: number;
    yearTarget: number | null;
    trendData: CoinTrendItem[];
}

export default function CoinStatsModal({
    open,
    onCancel,
    totalCoins,
    yearTarget,
    trendData,
}: CoinStatsModalProps) {
    // 折线图需要按时间正序（从左到右为时间递增）
    const chartData = [...trendData].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const streak = getCoinStreakStatus(trendData);
    const weekly = getWeeklyCoinGoal(trendData, streak.fires);
    const [view, setView] = useState<"trend" | "today">("trend");
    const [receipts, setReceipts] = useState<CoinReceiptEntry[]>([]);

    useEffect(() => {
        if (open) {
            setReceipts(readCoinReceipts());
        }
    }, [open]);

    const todayKey = dayjs().format("YYYY-MM-DD");
    const todayCoins = trendData
        .filter((item) => dayjs(item.date).format("YYYY-MM-DD") === todayKey)
        .reduce((sum, item) => sum + (item.coins || 0), 0);
    const receiptSum = sumCoinReceipts(receipts);
    const receiptGroups = groupCoinReceiptsByTopic(receipts);

    return (
        <Modal
            title="金币统计"
            open={open}
            footer={null}
            centered
            wrapClassName="coin-stats-modal-wrap"
            onCancel={onCancel}
            width={600}
        >
            <div className="coin-stats-summary">
                <div className="coin-stats-summary__main">
                    <Statistic
                        title="总金币数"
                        value={totalCoins}
                        suffix={
                            yearTarget != null ? (
                                <span className="coin-year-goal">/ {yearTarget}</span>
                            ) : undefined
                        }
                        valueStyle={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#1890ff",
                        }}
                    />
                    <div className="coin-stats-weekly">
                        <span className="coin-stats-weekly__label">
                            最近6天 {weekly.pastSixSum}/{WEEKLY_COIN_GOAL}
                        </span>
                        <div
                            className={`coin-weekly-tip${weekly.met ? " coin-weekly-tip--met" : ""}`}
                        >
                            {weekly.tip}
                        </div>
                    </div>
                </div>
                {streak.fires > 0 && (
                    <div className="coin-streak-hint">
                        <div className="coin-streak-hint__fires" aria-label={`连续 ${streak.fires} 天`}>
                            {"🔥".repeat(streak.fires)}
                        </div>
                        <div className="coin-streak-hint__text">今日金币 ×{streak.multiplier}</div>
                    </div>
                )}
            </div>
            <div style={{ marginTop: 24 }}>
                <div className="coin-stats-switch">
                    <button
                        type="button"
                        className={view === "trend" ? "is-active" : undefined}
                        onClick={() => setView("trend")}
                    >
                        最近30天趋势
                    </button>
                    <span className="coin-stats-switch__bar" />
                    <button
                        type="button"
                        className={view === "today" ? "is-active" : undefined}
                        onClick={() => setView("today")}
                    >
                        今日金币记录
                    </button>
                </div>
                {view === "today" ? (
                    <>
                        {receipts.length === 0 ? (
                            <div className="coin-receipt-empty">今天还没有金币记录</div>
                        ) : (
                            <div className="coin-receipt-list">
                                {receiptGroups.map((group) => {
                                    const title = group.topicTitle
                                        ? parseQuestionTitle(group.topicTitle).shortTitle
                                        : `题目 ${group.topicId}`;
                                    return (
                                        <div className="coin-receipt-item" key={group.topicId}>
                                            <div className="coin-receipt-item__title">{title}</div>
                                            <div className="coin-receipt-item__lines">
                                                {group.entries.map((entry, index) => (
                                                    <div key={`${entry.at}-${index}`}>
                                                        {formatCoinReceiptLine(entry)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="coin-receipt-footer">
                            小票合计 {receiptSum}，今日金币 {todayCoins}
                        </div>
                    </>
                ) : (
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                                try {
                                    return value.slice(5); // 显示 MM-DD
                                } catch {
                                    return value;
                                }
                            }}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            allowDecimals={false}
                            label={{
                                value: "金币",
                                angle: -90,
                                position: "insideLeft",
                                style: { fontSize: 12 },
                            }}
                        />
                        <Tooltip
                            formatter={(value: number | undefined) => [`${value ?? 0} 金币`, "金币"]}
                            labelFormatter={(label) => `日期: ${label}`}
                        />
                        <Line
                            type="monotone"
                            dataKey="coins"
                            stroke="#1890ff"
                            strokeWidth={2}
                            dot={{ fill: "#1890ff", r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
                )}
            </div>
        </Modal>
    );
}
